import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import {
  Settings,
  Users,
  Building2,
  Database,
  Mail,
  ClipboardList,
  MessageSquare,
  ShieldCheck,
  Shield,
  FileQuestion,
  Gamepad2,
  Network,
  Clock,
  Send,
  Activity,
  Gift,
  Target,
  Bell,
  Terminal,
  School,
  CreditCard,
  Flag,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  UserPlus,
  BookOpen,
  Server,
  Search,
  X,
  Calendar,
  Video,
  Sparkles,
  Megaphone,
  Film,
  Palette
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useOrganizationStore } from '../stores/organizationStore'
import useSidebarStore from '../stores/sidebarStore'
import { filterNavItemsByMode } from '../utils/sidebarMode'
import { isEducator } from '../utils/roles'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { useSidebarNavItems } from '../hooks/useSidebarNavItems'

const adminGroups = [
  {
    name: 'Core',
    icon: LayoutDashboard,
    items: [
      { id: 'overview', name: 'Overview', icon: Settings },
      { id: 'users', name: 'User Management', icon: Users },
      { id: 'organizations', name: 'Organizations', icon: Building2 },
      { id: 'schools', name: 'Schools', icon: School }
    ]
  },
  {
    name: 'User Engagement',
    icon: UserPlus,
    items: [
      { id: 'invitations', name: 'Invitations', icon: Mail },
      { id: 'waitlist', name: 'Waitlist', icon: ClipboardList },
      { id: 'referrals', name: 'Referrals', icon: Gift },
      { id: 'campaigns', name: 'Campaigns', icon: Target },
      { id: 'feature-announcements', name: 'Feature Announcements', icon: Sparkles },
      { id: 'marketing', name: 'Marketing', icon: Film },
      { id: 'coach-applications', name: 'Coach Applications', icon: GraduationCap },
      { id: 'feedback', name: 'Feedback', icon: MessageSquare },
      { id: 'fomo', name: 'FOMO Notifications', icon: Bell },
      { id: 'events', name: 'Events', icon: Calendar },
      { id: 'live-sessions', name: 'Live Sessions', icon: Video }
    ]
  },
  {
    name: 'Content & Learning',
    icon: BookOpen,
    items: [
      { id: 'questions', name: 'Questions', icon: FileQuestion },
      { id: 'challenge-types', name: 'Challenge Types', icon: Gamepad2 },
      { id: 'knowledge-graph', name: 'Knowledge Graph', icon: Network },
      { id: 'vaults', name: 'Vaults', icon: Database }
    ]
  },
  {
    name: 'System Operations',
    icon: Server,
    items: [
      { id: 'jobs', name: 'Scheduled Jobs', icon: Clock },
      { id: 'queue-monitor', name: 'Queue Monitor', icon: Activity },
      { id: 'email-management', name: 'Email Management', icon: Send },
      { id: 'database-query', name: 'Database Query', icon: Terminal },
      { id: 'data', name: 'Data Management', icon: Database }
    ]
  },
  {
    name: 'Security & Billing',
    icon: ShieldCheck,
    items: [
      { id: 'licensing/tiers', name: 'Licensing', icon: CreditCard },
      { id: 'whitelabeling', name: 'Whitelabeling', icon: Palette },
      { id: 'feature-flags', name: 'Feature Flags', icon: Flag },
      { id: 'audit-logs', name: 'Audit Logs', icon: ShieldCheck },
      { id: 'compliance', name: 'Compliance', icon: Shield }
    ]
  }
]

// Helper to find which group contains an item
const findGroupForItem = (itemId) => {
  for (const group of adminGroups) {
    if (
      group.items.some(
        (item) =>
          item.id === itemId || (item.id.startsWith('licensing/') && itemId.startsWith('licensing'))
      )
    ) {
      return group.name
    }
  }
  return null
}

const Sidebar = () => {
  const {
    isOpen,
    close,
    adminExpanded,
    toggleAdminExpanded,
    expandedGroups,
    toggleGroup,
    setGroupExpanded,
    roleMode,
    setRoleMode
  } = useSidebarStore()
  const {
    user,
    isImpersonating,
    myCoaches,
    myCoachPendingCount,
    coachNotificationCount,
    coachingNotificationCount,
    coachingActionCount
  } = useAuthStore()
  const { classManagementEnabled } = useFeatureFlags()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  // Feature flags
  const { enabled: calculatorEnabled } = useFeatureFlag('graphing_calculator')
  const { enabled: adaptiveTeachingEnabled } = useFeatureFlag('adaptive_teaching')
  const { enabled: tutorGroupsEnabled } = useFeatureFlag('tutor_groups')
  const { enabled: calendarEnabled, loading: calendarFlagLoading } =
    useFeatureFlag('scheduling_calendar')

  // Get current admin tab from hash
  const currentHash = location.hash.replace('#', '').split('?')[0]
  const isOnAdminPage = location.pathname === '/admin'

  // Filter admin groups based on search query
  const filteredAdminGroups = useMemo(() => {
    if (!searchQuery.trim()) return adminGroups

    const query = searchQuery.toLowerCase()
    return adminGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.name.toLowerCase().includes(query) ||
            item.id.toLowerCase().includes(query) ||
            group.name.toLowerCase().includes(query)
        )
      }))
      .filter((group) => group.items.length > 0)
  }, [searchQuery])

  // Check if we're in search mode (has active search with results)
  const isSearching = searchQuery.trim().length > 0

  // Auto-expand the group containing the active tab
  useEffect(() => {
    if (isOnAdminPage && currentHash && !isSearching) {
      const activeGroup = findGroupForItem(currentHash)
      if (activeGroup && !expandedGroups[activeGroup]) {
        setGroupExpanded(activeGroup, true)
      }
    } else if (isOnAdminPage && !currentHash && !isSearching) {
      // Default to overview which is in Core group
      if (!expandedGroups['Core']) {
        setGroupExpanded('Core', true)
      }
    }
  }, [isOnAdminPage, currentHash, setGroupExpanded, isSearching])

  // Clear search when navigating away from admin
  useEffect(() => {
    if (!isOnAdminPage) {
      setSearchQuery('')
    }
  }, [isOnAdminPage])

  // Show "My Coach" when the user has an active coach OR a not-yet-active coach relationship that
  // needs their attention (a pending agreement to sign, or a received invitation to accept).
  // Without the pending case, a student with only a pending_agreements coach can't reach My Coach
  // to sign — a deadlock. A coach can also be coached (dual role), so this is independent of isCoach.
  const hasCoaches = (myCoaches && myCoaches.length > 0) || myCoachPendingCount > 0

  // Check if user is a coach
  const isCoach = user?.isCoach

  const isGuardian = user?.accountType === 'guardian'

  // Coach/admin/org-staff may view the educator surfaces (class knowledge map, Adaptive Teaching).
  // Shared predicate with the EducatorRoute guard so nav visibility and route access can't drift.
  const canViewClassKnowledgeMap = isEducator(user)

  // The active org gates institution-only items (School Cockpit); see isInstitutionOrg.
  const activeOrganization = useOrganizationStore((s) => s.activeOrganization)

  const { navGroups, bottomItems } = useSidebarNavItems({
    user,
    isCoach,
    isGuardian,
    canViewClassKnowledgeMap,
    hasCoaches,
    classManagementEnabled,
    adaptiveTeachingEnabled,
    tutorGroupsEnabled,
    calculatorEnabled,
    coachingActionCount,
    coachingNotificationCount,
    coachNotificationCount,
    activeOrganization
  })

  // Student/Coach mode switch: a coach (or class instructor) is also a student, so the nav can show
  // one role's menus at a time to avoid clutter. Coaches default to Coach mode.
  const showModeToggle = canViewClassKnowledgeMap
  const effectiveMode = showModeToggle ? (roleMode ?? (isCoach ? 'coach' : 'student')) : 'student'
  const filterByMode = (items) =>
    showModeToggle ? filterNavItemsByMode(items, effectiveMode) : items
  const visibleGroups = navGroups
    .map((g) => ({ ...g, items: filterByMode(g.items) }))
    .filter((g) => g.items.length > 0)
  const visibleBottomItems = filterByMode(bottomItems)

  const switchMode = (mode) => {
    setRoleMode(mode)
    close()
    navigate(mode === 'coach' ? '/coach' : '/dashboard')
  }

  return (
    <>
      {/* Sidebar */}
      <div
        className={`
        fixed left-0 bottom-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${isImpersonating ? 'top-[104px]' : 'top-16'}
      `}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4">
            {/* Student / Coach mode switch (coaches & class instructors only) */}
            {showModeToggle && (
              <div className="mb-3 px-1">
                <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-900 rounded-lg">
                  {[
                    ['coach', 'Coach'],
                    ['student', 'Student']
                  ].map(([mode, label]) => (
                    <button
                      key={mode}
                      onClick={() => switchMode(mode)}
                      aria-pressed={effectiveMode === mode}
                      className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        effectiveMode === mode
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Grouped nav items */}
            {visibleGroups.map((group) => (
              <div key={group.label} className="mb-2">
                <div className="px-3 py-1 text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
                  {group.label}
                </div>
                {group.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.id}
                      to={item.href}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                          isActive
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100'
                        }`
                      }
                      onClick={close}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="flex-1">{item.name}</span>
                      {item.badge > 0 && (
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-primary-600 text-white min-w-[20px] text-center">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            ))}

            {/* Bottom items (Profile, Calculator) */}
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              {visibleBottomItems.map((item) => {
                const Icon = item.icon
                return (
                  <NavLink
                    key={item.id}
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                        isActive
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100'
                      }`
                    }
                    onClick={close}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1">{item.name}</span>
                  </NavLink>
                )
              })}
            </div>

            {/* Marketing now lives under Admin (#marketing). Admins reach it via the
                Administration submenu below; marketing-only users get a direct link. */}
            {user?.isMarketing && !user?.isAdmin && (
              <div className="pt-2">
                <NavLink
                  to="/admin#marketing"
                  onClick={close}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                      isActive
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100'
                    }`
                  }
                >
                  <Megaphone className="w-5 h-5" />
                  Marketing
                </NavLink>
              </div>
            )}

            {/* Admin Dashboard with submenus - only for admins */}
            {user?.isAdmin && (
              <div className="pt-2">
                <button
                  onClick={toggleAdminExpanded}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                    isOnAdminPage
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                  aria-label={adminExpanded ? 'Collapse admin menu' : 'Expand admin menu'}
                  aria-expanded={adminExpanded}
                >
                  <span className="flex items-center gap-3">
                    <Settings className="w-5 h-5" />
                    Administration
                  </span>
                  {adminExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  )}
                </button>

                {/* Collapsible Admin Groups */}
                <div
                  className={`overflow-hidden transition-all duration-200 ease-in-out ${
                    adminExpanded ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  {/* Search Input */}
                  <div className="mx-2 mt-2 mb-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search admin..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* No results message */}
                  {isSearching && filteredAdminGroups.length === 0 && (
                    <div className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 text-center">
                      No results for "{searchQuery}"
                    </div>
                  )}

                  <div className="ml-2 mt-1 space-y-1">
                    {filteredAdminGroups.map((group) => {
                      const GroupIcon = group.icon
                      // Auto-expand all groups when searching, otherwise use stored state
                      const isGroupExpanded = isSearching || expandedGroups[group.name]
                      const hasActiveItem = group.items.some(
                        (item) =>
                          isOnAdminPage &&
                          (currentHash === item.id ||
                            (!currentHash && item.id === 'overview') ||
                            (item.id.startsWith('licensing/') &&
                              currentHash.startsWith('licensing')))
                      )

                      return (
                        <div key={group.name}>
                          {/* Group Header */}
                          <button
                            onClick={() => toggleGroup(group.name)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-md transition-colors ${
                              hasActiveItem
                                ? 'text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800/50'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/30'
                            }`}
                            aria-label={
                              isGroupExpanded ? `Collapse ${group.name}` : `Expand ${group.name}`
                            }
                            aria-expanded={isGroupExpanded}
                          >
                            <span className="flex items-center gap-2">
                              <GroupIcon className="w-3.5 h-3.5" />
                              {group.name}
                            </span>
                            <ChevronRight
                              className={`w-3 h-3 transition-transform duration-200 ${
                                isGroupExpanded ? 'rotate-90' : ''
                              }`}
                            />
                          </button>

                          {/* Group Items - Collapsible */}
                          <div
                            className={`overflow-hidden transition-all duration-200 ease-in-out ${
                              isGroupExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className="ml-3 mt-0.5 border-l border-gray-200 dark:border-gray-700">
                              {group.items.map((subItem) => {
                                const SubIcon = subItem.icon
                                const isActiveSubItem =
                                  isOnAdminPage &&
                                  (currentHash === subItem.id ||
                                    (!currentHash && subItem.id === 'overview') ||
                                    (subItem.id.startsWith('licensing/') &&
                                      currentHash.startsWith('licensing')))
                                return (
                                  <NavLink
                                    key={subItem.id}
                                    to={`/admin#${subItem.id}`}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                                      isActiveSubItem
                                        ? 'text-gray-900 dark:text-gray-100 font-medium'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                                    }`}
                                    onClick={close}
                                  >
                                    <SubIcon className="w-4 h-4" />
                                    {subItem.name}
                                  </NavLink>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden" onClick={close} />
      )}
    </>
  )
}

export default Sidebar
